'use client';

/**
 * Presence bridge — a module-scoped mirror that carries workspace
 * presence ACROSS the `CanvasYjsProvider` boundary.
 *
 * The Y.Awareness lives inside `<CanvasYjsProvider>` (which wraps only
 * the canvas), but the rail's "Collaborate" badge and the Collaborate
 * panel both render OUTSIDE that provider. Rather than lift the
 * provider over the whole shell, a single canvas-side `<PresenceBridge>`
 * publishes the participant list here, and the rail + panel read it.
 *
 * Same decoupling pattern as `panel-events.ts`: a module store + a
 * `useSyncExternalStore` hook, plus an imperative channel the panel
 * uses to ask the canvas to follow a peer (the awareness write happens
 * canvas-side, where the provider is).
 */

import { useSyncExternalStore } from 'react';
import type { Participant } from './types';

/* ------------------------------------------------------------------ */
/* Participant mirror                                                 */
/* ------------------------------------------------------------------ */

let participants: Participant[] = [];
const participantSubs = new Set<() => void>();

export function publishParticipants(next: Participant[]): void {
  participants = next;
  participantSubs.forEach((fn) => fn());
}

function subscribeParticipants(cb: () => void): () => void {
  participantSubs.add(cb);
  return () => participantSubs.delete(cb);
}

/** Live remote-participant list (excludes self). Re-renders only when
 *  membership / focus changes, never on cursor movement. */
export function useParticipantsMirror(): Participant[] {
  return useSyncExternalStore(
    subscribeParticipants,
    () => participants,
    () => participants,
  );
}

/** Just the count — for the rail badge. */
export function usePresenceCount(): number {
  return useParticipantsMirror().length;
}

/**
 * One entry per PERSON. Presence lists CONNECTIONS, so a user with two
 * tabs open appears twice — which read as "@robutlerai @robutlerai" on
 * the taskbar hover card and doubled the presence dots. Every surface
 * that shows people (dots, avatar stacks, name lists) dedupes through
 * this; surfaces that genuinely care about connections (debugging) can
 * keep the raw list.
 */
export function uniqueParticipants(list: Participant[]): Participant[] {
  const seen = new Set<string>();
  return list.filter((p) => {
    const key = p.user.userId || p.user.username || p.user.peerId;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/* ------------------------------------------------------------------ */
/* Locate channel (panel → canvas)                                    */
/* ------------------------------------------------------------------ */

// One-shot "jump my viewport to where this peer is" — driven from the
// panel, executed canvas-side (where ReactFlow + awareness live). A
// one-shot jump avoids the 60Hz re-snap problem of continuous follow;
// continuous follow can layer on later.
const locateSubs = new Set<(peerId: string) => void>();

export function requestLocate(peerId: string): void {
  locateSubs.forEach((fn) => fn(peerId));
}

export function subscribeLocateRequest(
  cb: (peerId: string) => void,
): () => void {
  locateSubs.add(cb);
  return () => locateSubs.delete(cb);
}

/* ------------------------------------------------------------------ */
/* View settings (panel → canvas overlays)                            */
/* ------------------------------------------------------------------ */

export interface PresenceSettings {
  showCursors: boolean;
  showViewports: boolean;
}

let settings: PresenceSettings = { showCursors: true, showViewports: false };
const settingsSubs = new Set<() => void>();

export function setPresenceSetting<K extends keyof PresenceSettings>(
  key: K,
  value: PresenceSettings[K],
): void {
  if (settings[key] === value) return;
  settings = { ...settings, [key]: value };
  settingsSubs.forEach((fn) => fn());
}

export function usePresenceSettings(): PresenceSettings {
  return useSyncExternalStore(
    (cb) => {
      settingsSubs.add(cb);
      return () => settingsSubs.delete(cb);
    },
    () => settings,
    () => settings,
  );
}
